import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';

function Wordmark({ size = 'md' }: { size?: 'md' | 'sm' }) {
  const box = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm';
  const text = size === 'sm' ? 'text-base' : 'text-lg';
  return (
    <span className="flex items-center gap-2 flex-shrink-0">
      <span className={`${box} bg-berry-500 rounded-lg flex items-center justify-center shadow-sm`} aria-hidden="true">
        <span className="text-white font-serif font-semibold">PF</span>
      </span>
      <span className={`font-serif font-semibold text-ink-900 ${text} tracking-tight`}>PitchFriendly</span>
    </span>
  );
}

export default function MarketingLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      {/* Skip link for keyboard users */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-lg focus:bg-ink-900 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>

      {/* Subtle paper grain — fixed, non-interactive, above backgrounds but below header */}
      <div className="pointer-events-none fixed inset-0 z-[2] bg-grain opacity-[0.04] mix-blend-multiply" aria-hidden="true" />

      <header
        className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ease-editorial ${
          scrolled
            ? 'bg-paper/85 backdrop-blur border-b border-ink-100 shadow-sm'
            : 'bg-paper/0 border-b border-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" aria-label="PitchFriendly home">
            <Wordmark />
          </Link>

          <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
            <Link to="/features" className="text-sm text-ink-600 hover:text-ink-900 transition-colors duration-150 font-medium">Features</Link>
            <Link to="/pricing" className="text-sm text-ink-600 hover:text-ink-900 transition-colors duration-150 font-medium">Pricing</Link>
            <Link to="/about" className="text-sm text-ink-600 hover:text-ink-900 transition-colors duration-150 font-medium">About</Link>
            <Link to="/contact" className="text-sm text-ink-600 hover:text-ink-900 transition-colors duration-150 font-medium">Contact</Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard" className="px-4 py-2 text-sm bg-berry-500 text-white rounded-lg hover:bg-berry-600 font-medium shadow-sm hover:shadow-md transition-all duration-150 ease-editorial hover:-translate-y-px active:translate-y-0">
                Go to App
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-ink-600 hover:text-ink-900 font-medium transition-colors duration-150">Log in</Link>
                <Link to="/register" className="px-4 py-2 text-sm bg-berry-500 text-white rounded-lg hover:bg-berry-600 font-medium shadow-sm hover:shadow-md transition-all duration-150 ease-editorial hover:-translate-y-px active:translate-y-0">
                  Start free
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-ink-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <svg className="w-5 h-5 text-ink-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <nav id="mobile-menu" aria-label="Mobile" className="md:hidden border-t border-ink-100 bg-paper px-4 py-3 space-y-1">
            <Link to="/features" className="block px-3 py-2.5 text-sm text-ink-700 rounded-lg hover:bg-ink-100" onClick={() => setMobileOpen(false)}>Features</Link>
            <Link to="/pricing" className="block px-3 py-2.5 text-sm text-ink-700 rounded-lg hover:bg-ink-100" onClick={() => setMobileOpen(false)}>Pricing</Link>
            <Link to="/about" className="block px-3 py-2.5 text-sm text-ink-700 rounded-lg hover:bg-ink-100" onClick={() => setMobileOpen(false)}>About</Link>
            <Link to="/contact" className="block px-3 py-2.5 text-sm text-ink-700 rounded-lg hover:bg-ink-100" onClick={() => setMobileOpen(false)}>Contact</Link>
            <div className="border-t border-ink-100 mt-2 pt-2">
              {isAuthenticated ? (
                <Link to="/dashboard" className="block px-3 py-2.5 text-sm font-medium text-berry-700" onClick={() => setMobileOpen(false)}>Go to App</Link>
              ) : (
                <>
                  <Link to="/login" className="block px-3 py-2.5 text-sm text-ink-700 rounded-lg hover:bg-ink-100" onClick={() => setMobileOpen(false)}>Log in</Link>
                  <Link to="/register" className="block px-3 py-2.5 text-sm font-medium text-berry-700" onClick={() => setMobileOpen(false)}>Start free</Link>
                </>
              )}
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">{children}</main>

      <footer className="bg-ink-900 text-ink-300 py-12 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4" aria-label="PitchFriendly home">
                <span className="w-7 h-7 bg-berry-500 rounded-md flex items-center justify-center" aria-hidden="true">
                  <span className="text-white font-serif font-semibold text-xs">PF</span>
                </span>
                <span className="text-white font-serif font-semibold">PitchFriendly</span>
              </Link>
              <p className="text-sm leading-relaxed text-ink-300">PR software built for modern agencies and communications teams.</p>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Get started</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-ink-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-400">
            <span>© {new Date().getFullYear()} PitchFriendly. All rights reserved.</span>
            <nav aria-label="Footer legal" className="flex items-center gap-4">
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
